// SUBMIT-TIME CONTRACT CHECK — the live-path half of the validator fork (cc-31).
//
// The fork, in one line: `validateWizardInput` knew how to catch real errors and nothing on the
// way to a rendered document ever called it, so the contract worked as documentation rather than
// a guard. The answer is deliberately NOT one call site but two, because the two kinds of
// producer differ in what a failure costs:
//
//   · imports / fixtures / our own runs → `assertRenderable` in `render/assemble.jsx` THROWS.
//     Nobody is harmed by a loud refusal, and a wrong page is worse than no page.
//   · a person mid-wizard → THIS function, called at submit, BEFORE the passport is built.
//     Throwing at them would cost twelve minutes of their work to save one odd date. So this
//     one never throws: it returns the problems, addressed to the screen and the row that can
//     still be fixed.
//
// The addressing is the whole point, and it is also the trap this module exists to absorb.
// Contract indices are OLDEST-FIRST (idx N−1 = now, D&R s485.d1), while step 2 collects rows
// NEWEST-FIRST ("start with now, work backwards"). Handing Track C a raw contract index would
// have it highlight the wrong row — the mirror image of the right one — and the mistake is
// invisible in the middle of the list and maximal at its ends. Every direction conversion the
// wizard needs lives here and in `toWizardInput`, nowhere else.
//
// Step 3 (the deck) is oldest-first already, so slider/text/button indices pass through.

import { validateWizardInput, wizardInputWarnings } from '../contracts.js';
import { formatAddress } from './address.js';

/**
 * @typedef {Object} SubmitProblem
 * @property {string} message   the contract's own wording, unchanged
 * @property {string} field     'stages' | 'sliders' | 'text' | 'buttons' | 'input'
 * @property {number} step      2 (stages) · 3 (sliders/text/buttons) · 0 when not addressable
 * @property {number|null} contractIndex  oldest-first index the contract complained about
 * @property {number|null} row  the index to highlight ON THAT SCREEN — reversed for step 2
 * @property {SubmitTarget|null} target  the field INSIDE the row (B10-2); `null` for whole-input problems
 * @property {string|null} address  ready screen address (`#s2/N`, `#s3/N/a|b|c`); `null` when not addressable
 */

/**
 * АДРЕС ВНУТРИ РЯДА (B10-2). Раньше `SubmitProblem` адресовал только ряд, а слой / группа / T-id
 * лежали только в `message` — строке, которую парсить нельзя: её сверяют тесты. Разбор делается
 * ЗДЕСЬ, один раз, по форме имени поля в сообщении контракта (`sliders[2].Growth`,
 * `buttons[1].BG3`, `stages[0].startY`), и наружу уходит структура. Сам `message` не меняется.
 *
 * @typedef {Object} SubmitTarget
 * @property {string} [layer]  ключ слоя — для `sliders`
 * @property {string} [group]  id группы `BG1…BG4` — для `buttons`
 * @property {string} [tid]    `T1…T3` — для `text`
 * @property {'name'|'date'|'flag'|'row'} [part]  что в ряду шага 2: имя, дата, булев флаг или ряд целиком
 * @property {Array<'start'|'end'>} [dates]  какие даты ряда шага 2; перевёрнутый интервал — обе
 */

const STEP_OF = { stages: 2, sliders: 3, text: 3, buttons: 3 };

// Панели деки шага 3 по полю ряда: A — ползунки, B — кнопки, C — поля текста (`deckNav.js`, `PANE_SUBS`).
const PANE_OF = { sliders: 'a', buttons: 'b', text: 'c' };

/** Поле внутри ряда из имени поля в сообщении контракта. */
function targetOf(field, key, message) {
  if (field === 'sliders') return key ? { layer: key } : {};
  if (field === 'buttons') return key ? { group: key } : {};
  if (field === 'text') return key ? { tid: key } : {};
  // stages
  if (key === 'name') return { part: 'name' };
  if (key === 'startY') return { part: 'date', dates: ['start'] };
  if (key === 'endY') return { part: 'date', dates: ['end'] };
  if (key) return { part: 'flag' }; // current / openStart
  if (/interval is inverted/.test(message)) return { part: 'date', dates: ['start', 'end'] };
  return { part: 'row' };
}

/**
 * Адрес подэкрана: шаг 2 — ряд экрана, счёт с единицы от нового (`#s2/N`, `StepTwo.jsx`);
 * шаг 3 — карточка в порядке деки (oldest-first, индекс контракта насквозь) и панель поля.
 */
function addressOf(field, contractIndex, row) {
  if (field === 'stages') return formatAddress({ step: 2, card: row + 1 });
  return formatAddress({ step: 3, card: contractIndex + 1, sub: PANE_OF[field] });
}

/**
 * Contract check for the live wizard path. NEVER throws and never blocks by itself — the caller
 * decides what to do with the answer (Track C: keep the person on the wizard, send them to
 * `problem.step`, highlight `problem.row`).
 *
 * @param {import('../contracts.js').WizardInput} input  what `toWizardInput` just produced
 * ВТОРОЙ КАНАЛ — предупреждения (cc-31, найдено QA). `wizardInputWarnings` знает ровно те
 * инварианты, на которых сегодня ломался темпоральный слой: ни одного `current`, несколько
 * `current`, `current` не на последнем этапе, перевёрнутые или равные начала, перехлёст, щель.
 * Замер показал, что **в проде его не звал никто** — ни `assertRenderable`, ни этот модуль:
 * детектор, который всё знал и молчал, потому что мёртв. Теперь он возвращается здесь,
 * отдельным полем и с той же адресацией строк, что и ошибки. Предупреждение НЕ роняет и НЕ
 * блокирует (`ok` от него не зависит): импортированные биографии обязаны продолжать рисоваться,
 * а человек в визарде — видеть, что дата выглядит странно, и решать сам.
 *
 * @returns {{ ok: boolean, problems: SubmitProblem[], warnings: SubmitProblem[] }}
 */
export function wizardSubmitCheck(input) {
  const problems = validateWizardInput(input);
  const N = (input && input.wizard && Array.isArray(input.wizard.sliders))
    ? input.wizard.sliders.length
    : 0;

  // Ошибки контракта НАЧИНАЮТСЯ с поля (`stages[2].startY …`), а предупреждения формулируются
  // человеческой фразой и называют этап в середине: «gap of 5y between stages[0] (ends …) and
  // stages[1] (starts …)». Якорь `^` их не видел, и весь канал приходил с `step: 0` — то есть
  // технически возвращался, а адресовать человека было некуда. Поэтому: у ошибок якорь, у
  // предупреждений — поиск по строке, и берётся ПОСЛЕДНИЙ названный индекс: в парных фразах
  // (щель, перехлёст, порядок) второй этап это тот, чью дату человек и правит.
  const address = (message, anchored = true) => {
    const all = [...message.matchAll(/(stages|sliders|text|buttons)\[(\d+)\]/g)];
    const m = anchored ? (message.match(/^(stages|sliders|text|buttons)\[(\d+)\]/) || null)
      : (all.length ? all[all.length - 1] : null);
    if (!m) return { message, field: 'input', step: 0, contractIndex: null, row: null };
    const field = m[1];
    const contractIndex = Number(m[2]);
    const row = field === 'stages' && N > 0 ? N - 1 - contractIndex : contractIndex;
    return { message, field, step: STEP_OF[field], contractIndex, row };
  };

  return {
    ok: problems.length === 0,
    warnings: wizardInputWarnings(input).map((w) => address(w, false)),
    problems: problems.map((message) => {
      const m = message.match(/^(stages|sliders|text|buttons)\[(\d+)\](?:\.(\w+))?/);
      if (!m) {
        // whole-input problems ("wizard.sliders must be a non-empty array", the stage ceiling,
        // length mismatches) — real, but not attached to one row.
        return { message, field: 'input', step: 0, contractIndex: null, row: null, target: null, address: null };
      }
      const field = m[1];
      const contractIndex = Number(m[2]);
      // ← the reverse, and the only reason this module is not two lines inline
      const row = field === 'stages' && N > 0 ? N - 1 - contractIndex : contractIndex;
      return {
        message, field, step: STEP_OF[field], contractIndex, row,
        target: targetOf(field, m[3] ?? null, message),
        address: addressOf(field, contractIndex, row),
      };
    }),
  };
}
